import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lightbulb, Check, Lock, ChevronDown, Sparkles } from "lucide-react";
import "./ExplainPanel.css";

const FREE_SOURCES = [
  "Your Parish Profile",
  "Your denomination",
  "The current liturgical season",
  "Your planning request",
];

export default function ExplainPanel({ explain, subscribed, memoryPreview }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(true);
  if (!explain && !memoryPreview?.length) return null;

  const used = explain?.sources_used || [];
  const resolved = explain?.resolved || [];
  const conflicts = explain?.conflicts || [];
  const memoryItems = resolved.filter((r) =>
    (r.source || "").toLowerCase().includes("memory"));
  const confVals = resolved.map((r) => r.confidence).filter((c) => c != null);
  const avgConf = confVals.length
    ? Math.round((confVals.reduce((a, b) => a + b, 0) / confVals.length) * 100)
    : null;

  return (
    <div className="xp report-no-print" data-testid="explain-panel">
      <button className="xp-head" onClick={() => setOpen(!open)} data-testid="explain-toggle">
        <span className="xp-title"><Lightbulb size={18} /> Why did Ordo AI™ recommend this?</span>
        <ChevronDown size={18} className={`xp-chev${open ? " up" : ""}`} />
      </button>

      {open && (
        <div className="xp-body">
          {!subscribed ? (
            <div data-testid="explain-free">
              <p className="xp-lead">This recommendation was based on:</p>
              <ul className="xp-checks">
                {FREE_SOURCES.map((s) => (
                  <li key={s}><Check size={15} /> {s}</li>
                ))}
              </ul>

              {memoryPreview?.length > 0 && (
                <div className="xp-learned" data-testid="explain-learned">
                  <h4><Sparkles size={15} /> Ordo AI™ learned about your parish</h4>
                  <ul className="xp-checks">
                    {memoryPreview.map((m, i) => (
                      <li key={i}><Check size={15} /> <b>{m.title}:</b> {m.value}</li>
                    ))}
                  </ul>
                  <p className="xp-note">This intelligence was discovered from this generation.</p>
                </div>
              )}

              <div className="xp-upsell" data-testid="explain-upsell">
                <p>With <b>Parish Pro</b>, Ordo AI™ remembers these insights permanently and uses them to improve every future recommendation — your parish's own growing institutional memory.</p>
                <button className="xp-cta" onClick={() => navigate("/account")} data-testid="explain-upgrade-btn">
                  Unlock Parish Memory™
                </button>
              </div>
            </div>
          ) : (
            <div data-testid="explain-pro">
              <div className="xp-grid">
                <div className="xp-col">
                  <h4>Sources Used</h4>
                  <ul className="xp-checks">
                    {used.map((s) => <li key={s}><Check size={15} /> {s}</li>)}
                  </ul>
                </div>
                {avgConf != null && (
                  <div className="xp-col xp-conf">
                    <h4>Confidence</h4>
                    <div className="xp-conf-num" data-testid="explain-confidence">{avgConf}%</div>
                  </div>
                )}
              </div>

              {explain?.reasoning && (
                <div className="xp-block">
                  <h4>Reasoning</h4>
                  <p>{explain.reasoning}</p>
                </div>
              )}

              {memoryItems.length > 0 && (
                <div className="xp-block" data-testid="explain-memory-used">
                  <h4>Parish Memory Used</h4>
                  <ul className="xp-mem">
                    {memoryItems.map((m, i) => (
                      <li key={i}>
                        {m.locked && <Lock size={13} className="xp-lock" />}
                        <b>{m.key.replace(/_/g, " ")}</b> → {String(m.value)}
                        <span className="xp-pct">{Math.round((m.confidence || 0) * 100)}%</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="xp-block">
                <h4>Conflicts</h4>
                {conflicts.length === 0 ? (
                  <p className="xp-none">None — all sources aligned.</p>
                ) : (
                  <ul className="xp-conflicts">
                    {conflicts.map((c, i) => (
                      <li key={i}>
                        The current request overrode the usual preference for <b>{c.key.replace(/_/g, " ")}</b>
                        {c.winner ? ` (used "${c.winner.value}" instead of "${c.overridden?.value}").` : "."}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="xp-actions">
                <button className="xp-btn" onClick={() => navigate("/parish-memory")} data-testid="explain-view-memory">View Memory</button>
                <button className="xp-btn ghost" onClick={() => navigate("/parish-memory")} data-testid="explain-edit-memory">Edit Memory</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
